import { useState } from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  setCompletedCompliance,
  useUpdateCompliance,
} from "../services/compliance";
import { isApiError } from "@/shared/api";

type Step = "welcome" | "pep" | "fatca" | "success";

interface ComplianceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ComplianceModal({ isOpen, onClose }: ComplianceModalProps) {
  const [currentStep, setCurrentStep] = useState<Step>("welcome");
  const [isPep, setIsPep] = useState<boolean | null>(null);

  const { mutateAsync: submitCompliance, isPending: isSubmitting } =
    useUpdateCompliance();

  const handleStart = () => {
    setCurrentStep("pep");
  };

  const handlePepAnswer = (answer: boolean) => {
    setIsPep(answer);
    setCurrentStep("fatca");
  };

  const handleFatcaAnswer = async (answer: boolean) => {
    try {
      await submitCompliance({
        isPep: isPep ?? false,
        isFatca: answer,
      });
      setCompletedCompliance();
      setCurrentStep("success");
    } catch (error) {
      if (isApiError(error)) {
        toast.error(error.message);
      } else {
        toast.error(
          "Failed to update compliance information. Please try again."
        );
      }
    }
  };

  const handleClose = () => {
    setCurrentStep("welcome");
    setIsPep(null);
    onClose();
  };

  const canClose = currentStep === "success";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && canClose) {
          handleClose();
        }
      }}>
      <DialogContent
        className="sm:max-w-[420px] bg-[#1F242A] border-white/10 text-white p-0 gap-0"
        onInteractOutside={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) {
            e.preventDefault();
          }
        }}>
        {currentStep === "welcome" && <WelcomeStep onStart={handleStart} />}

        {currentStep === "pep" && <PepStep onAnswer={handlePepAnswer} />}

        {currentStep === "fatca" && (
          <FatcaStep onAnswer={handleFatcaAnswer} isSubmitting={isSubmitting} />
        )}

        {currentStep === "success" && <SuccessStep onClose={handleClose} />}
      </DialogContent>
    </Dialog>
  );
}

interface WelcomeStepProps {
  onStart: () => void;
}

function WelcomeStep({ onStart }: WelcomeStepProps) {
  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            KYC Requirement Update
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <p className="text-sm text-white/80 text-center">Welcome to Nommia!</p>

        <p className="text-sm text-white/60 text-center leading-relaxed">
          We're excited to have you. To give you the best experience possible,
          we have a few quick questions to help us tailor the app to your needs.
        </p>

        <p className="text-sm text-white/60 text-center leading-relaxed">
          Tap Start to finish setting up your profile, it will take less than 5
          minutes!
        </p>

        <Button
          onClick={onStart}
          className="w-full mt-6 bg-white text-black hover:bg-white/90 rounded-full py-6 font-medium">
          Start
        </Button>
      </div>
    </>
  );
}

interface PepStepProps {
  onAnswer: (answer: boolean) => void;
}

function PepStep({ onAnswer }: PepStepProps) {
  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            KYC Requirement Update
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <p className="text-sm text-white/80 text-center">
          Are you a politically exposed person (PEP)?
        </p>

        <p className="text-sm text-white/60 text-center leading-relaxed">
          A PEP is an individual who holds, or has held in the last 12 months, a
          prominent public position (such as a senior government official,
          politician, or military officer). This also applies if you are an
          immediate family member or a close business associate of such a
          person.
        </p>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => onAnswer(true)}
            variant="outline"
            className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full py-6 font-medium">
            Yes
          </Button>
          <Button
            onClick={() => onAnswer(false)}
            variant="outline"
            className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full py-6 font-medium">
            No
          </Button>
        </div>
      </div>
    </>
  );
}

interface FatcaStepProps {
  onAnswer: (answer: boolean) => void;
  isSubmitting: boolean;
}

function FatcaStep({ onAnswer, isSubmitting }: FatcaStepProps) {
  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            KYC Requirement Update
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <p className="text-sm text-white/80 text-center">
          Are you a U.S. Person for tax purposes (FATCA)?
        </p>

        <p className="text-sm text-white/60 text-center leading-relaxed">
          Under the Foreign Account Tax Compliance Act (FATCA), we are required
          to determine if you are a "U.S. Person." This generally includes U.S.
          citizens (even those living abroad), Green Card holders, or
          individuals who spend a significant amount of time in the U.S. each
          year.
        </p>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => onAnswer(true)}
            disabled={isSubmitting}
            variant="outline"
            className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full py-6 font-medium disabled:opacity-50">
            {isSubmitting ? "Loading..." : "Yes"}
          </Button>
          <Button
            onClick={() => onAnswer(false)}
            disabled={isSubmitting}
            variant="outline"
            className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full py-6 font-medium disabled:opacity-50">
            {isSubmitting ? "Loading..." : "No"}
          </Button>
        </div>
      </div>
    </>
  );
}

interface SuccessStepProps {
  onClose: () => void;
}

function SuccessStep({ onClose }: SuccessStepProps) {
  return (
    <>
      <DialogHeader className="p-6 pb-0">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
            <Check className="w-6 h-6 text-green-500" />
          </div>
          <DialogTitle className="text-lg font-semibold">
            You're all set!
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="p-6 pt-4 space-y-4">
        <p className="text-sm text-white/60 text-center leading-relaxed">
          Thank you for completing those questions. Your profile is now ready
          and you have full access to the Nommia platform.
        </p>

        <p className="text-sm text-white/60 text-center leading-relaxed">
          Explore your dashboard and start your journey today!
        </p>

        <Button
          onClick={onClose}
          className="w-full mt-6 bg-white text-black hover:bg-white/90 rounded-full py-6 font-medium">
          Get Started
        </Button>
      </div>
    </>
  );
}
